import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Users, ShoppingCart, Smartphone, Gift, BarChart3, Calendar, BellRing, Mail, UtensilsCrossed, ClipboardList, Monitor, Link, CreditCard, TrendingUp, RefreshCw, ShieldCheck, Lock, Unlock, MessageSquare, Zap, CheckCircle, XCircle, AlertTriangle, Search, Rocket, Gem } from 'lucide-react';
import InstallPWA from './InstallPWA';
import SiretLookup from './SiretLookup';
import './Signup.css';

function Signup() {
  const [selectedOffer, setSelectedOffer] = useState('premium');
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    entreprise: '',
    adresse: '',
    numero_siret: '',
    code_promo: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [promoCodeApplied, setPromoCodeApplied] = useState(null);
  const [promoCodeLoading, setPromoCodeLoading] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState('');
  const [stripeCheckoutUrl, setStripeCheckoutUrl] = useState(null);

  // Référence pour le formulaire d'inscription
  const formSectionRef = useRef(null);

  // Fonction pour scroller vers le formulaire
  const scrollToForm = () => {
    if (formSectionRef.current) {
      setTimeout(() => {
        formSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 300);
    }
  };

  const offers = [
    {
      id: 'premium',
      name: 'Premium',
      badge: 'TOUT COMPRIS - SANS LIMITE',
      badgeIcon: Gift,
      title: 'Pack Complet E-Shops Pro',
      subtitle: 'Utilisateurs illimités • Toutes fonctionnalités incluses',
      hasPaymentOptions: false,
      price: 599,
      priceLabel: '/mois',
      setupFee: 1050,
      developmentCost: 0,
      commitment: '36 mois',
      hasTrial: false,
      popular: true,
      color: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      description: 'Accédez à TOUS les modules E-Shops sans aucune restriction. Utilisateurs illimités, fonctionnalités illimitées, mises à jour incluses.',
      features: [
        {
          icon: Users,
          title: 'Utilisateurs illimités',
          description: 'Ajoutez autant de collaborateurs que vous voulez, sans frais supplémentaires'
        },
        {
          icon: ShoppingCart,
          title: 'E-commerce complet',
          description: 'Boutique en ligne, gestion produits, stocks, commandes et paiements intégrés'
        },
        {
          icon: Smartphone,
          title: 'Application mobile PWA',
          description: 'Votre app sur iOS, Android et desktop - installable sur tous les appareils'
        },
        {
          icon: Gift,
          title: 'Carte de fidélité digitale',
          description: 'Programme fidélité avec points, tampons, récompenses et offres VIP'
        },
        {
          icon: BarChart3,
          title: 'CRM & Gestion clients',
          description: 'Base clients centralisée, historique, segments et communication ciblée'
        },
        {
          icon: Calendar,
          title: 'Réservation & Agenda',
          description: 'Prise de rendez-vous en ligne, planning équipe et rappels automatiques'
        },
        {
          icon: BellRing,
          title: 'Notifications push illimitées',
          description: 'Alertez vos clients en temps réel : promos, commandes, actualités'
        },
        {
          icon: Mail,
          title: 'Emailing & SMS marketing',
          description: 'Campagnes email et SMS incluses pour fidéliser vos clients'
        },
        {
          icon: UtensilsCrossed,
          title: 'Click & Collect / Livraison',
          description: 'Commande en ligne avec retrait ou livraison, zones et créneaux configurables'
        },
        {
          icon: ClipboardList,
          title: 'Devis & Factures',
          description: 'Génération automatique de devis et factures conformes'
        },
        {
          icon: Monitor,
          title: 'Borne de commande',
          description: 'Interface tactile pour vos clients sur place (restaurants, commerces)'
        },
        {
          icon: Link,
          title: 'QR Codes & Menus digitaux',
          description: 'Menus interactifs, cartes digitales et commande à table'
        },
        {
          icon: CreditCard,
          title: 'Logiciel de caisse inclus',
          description: 'Encaissement, tickets, gestion espèces/CB et clôture de caisse'
        },
        {
          icon: TrendingUp,
          title: 'Statistiques avancées',
          description: 'Tableaux de bord, rapports de ventes et analyses détaillées'
        },
        {
          icon: RefreshCw,
          title: 'Mises à jour incluses',
          description: 'Nouvelles fonctionnalités ajoutées régulièrement, sans coût supplémentaire'
        },
        {
          icon: ShieldCheck,
          title: 'Support prioritaire 7j/7',
          description: 'Assistance technique réactive par téléphone, email et chat'
        }
      ],
      advantages: [
        '✓ Utilisateurs illimités inclus',
        '✓ Toutes les fonctionnalités E-Shops',
        '✓ Mises à jour gratuites',
        '✓ Formation complète offerte',
        '✓ Hébergement France inclus',
        '✓ Votre code vous appartient'
      ]
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis';
    if (!formData.email.trim()) newErrors.email = 'L\'email est requis';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email invalide';
    if (!formData.telephone.trim()) newErrors.telephone = 'Le téléphone est requis';
    if (!formData.entreprise.trim()) newErrors.entreprise = 'L\'entreprise est requise';
    if (!formData.adresse.trim()) newErrors.adresse = 'L\'adresse est requise';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fonction pour valider et appliquer le code partenaire
  const applyPromoCode = async () => {
    if (!formData.code_promo.trim()) {
      setPromoCodeError('Veuillez entrer un code');
      return;
    }

    setPromoCodeLoading(true);
    setPromoCodeError('');
    setPromoCodeApplied(null);

    try {
      const offerDetails = getSelectedOfferDetails();
      const response = await axios.post('/api/validate-promo-code', {
        code: formData.code_promo,
        offerId: selectedPaymentOption || selectedOffer,
        setupFee: offerDetails.setupFee,
        developmentCost: offerDetails.developmentCost || 0,
        monthlyPrice: offerDetails.price
      });

      if (response.data.valid) {
        setPromoCodeApplied(response.data);
        setPromoCodeError('');

        // Enregistrer le client ET générer le lien Stripe dès l'application du code partenaire
        try {
          const registerResponse = await axios.post('/api/register-lead', {
            ...formData,
            offer: selectedPaymentOption,
            offerDetails: offerDetails,
            promoCodeApplied: response.data
          });

          if (registerResponse.data.checkoutUrl) {
            setStripeCheckoutUrl(registerResponse.data.checkoutUrl);
            console.log('Client enregistré et lien Stripe généré');
          }
        } catch (leadError) {
          console.error('Erreur enregistrement:', leadError);
          // Ne pas bloquer si l'enregistrement échoue (champs non remplis par exemple)
        }
      } else {
        setPromoCodeError(response.data.message || 'Code invalide');
        setPromoCodeApplied(null);
      }
    } catch (error) {
      console.error('Erreur validation code promo:', error);
      setPromoCodeError(error.response?.data?.error || 'Erreur lors de la validation du code');
      setPromoCodeApplied(null);
    } finally {
      setPromoCodeLoading(false);
    }
  };

  // Réinitialiser le code promo et le lien Stripe quand l'offre change
  useEffect(() => {
    setPromoCodeApplied(null);
    setPromoCodeError('');
    setStripeCheckoutUrl(null);
    setFormData(prev => ({ ...prev, code_promo: '' }));
  }, [selectedPaymentOption]);

  // Fonction pour gérer l'installation de la PWA
  const handleInstallApp = async () => {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isIOS) {
      // Pour iOS, afficher les instructions
      setShowIOSInstructions(true);
    } else if (window.deferredPrompt) {
      // Pour Android/Chrome avec deferredPrompt disponible
      try {
        await window.deferredPrompt.prompt();
        const { outcome } = await window.deferredPrompt.userChoice;
        console.log('Installation:', outcome);
        window.deferredPrompt = null;
      } catch (error) {
        console.error('Erreur installation:', error);
        alert('Pour installer l\'application :\n\n' +
              'Chrome Android: Menu (⋮) → "Ajouter à l\'écran d\'accueil"\n' +
              'Firefox Android: Menu → "Installer"\n' +
              'Samsung Internet: Menu → "Ajouter page à"');
      }
    } else {
      // Pas de deferredPrompt disponible
      alert('Pour installer l\'application :\n\n' +
            'Chrome Android: Menu (⋮) → "Ajouter à l\'écran d\'accueil"\n' +
            'Firefox Android: Menu → "Installer"\n' +
            'Samsung Internet: Menu → "Ajouter page à"\n' +
            'Safari iOS: Bouton Partager → "Sur l\'écran d\'accueil"');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedOffer) {
      alert('Veuillez sélectionner une offre');
      return;
    }

    const offer = offers.find(o => o.id === selectedOffer);

    if (offer.hasPaymentOptions && !selectedPaymentOption) {
      alert('Veuillez choisir une formule de paiement');
      return;
    }

    if (!validateForm()) {
      alert('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    setLoading(true);

    try {
      // Si le lien Stripe a déjà été généré (via "Appliquer"), rediriger directement
      if (stripeCheckoutUrl) {
        console.log('Redirection vers le lien Stripe existant');
        window.location.href = stripeCheckoutUrl;
        return;
      }

      let offerDetails = offer;

      // Si l'offre a des options de paiement, utiliser l'option sélectionnée
      if (offer.hasPaymentOptions) {
        const paymentOption = offer.paymentOptions.find(opt => opt.id === selectedPaymentOption);
        offerDetails = {
          ...offer,
          ...paymentOption,
          id: selectedPaymentOption
        };
      }

      const response = await axios.post('/api/signup', {
        ...formData,
        offer: offerDetails.id,
        offerDetails: offerDetails,
        promoCodeApplied: promoCodeApplied
      });

      if (response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        alert('Erreur lors de la création du lien de paiement');
      }
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      alert(error.response?.data?.error || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const selectedOfferData = offers.find(o => o.id === selectedOffer);
  const getSelectedOfferDetails = () => {
    return selectedOfferData;
  };

  return (
    <div className="signup-container">
      <div className="signup-hero">
        <div className="hero-badge"><Gift size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> OFFRE TOUT COMPRIS</div>
        <h1>Tous les outils E-Shops, un seul prix</h1>
        <p className="hero-subtitle">Utilisateurs illimités, fonctionnalités illimitées, mises à jour incluses</p>
        <div className="hero-benefits">
          <span><Users size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Utilisateurs illimités</span>
          <span><Rocket size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> +15 modules inclus</span>
          <span><RefreshCw size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Mises à jour gratuites</span>
        </div>

        {/* Device Mockups */}
        <div className="hero-mockup">
          <div className="mockup-container">
            <div className="mockup-phone">
              <div className="mockup-phone-screen">
                <div className="mockup-screen-header"></div>
                <div className="mockup-screen-content">
                  <div className="mockup-screen-line"></div>
                  <div className="mockup-screen-line short"></div>
                  <div className="mockup-screen-line"></div>
                  <div className="mockup-screen-line short"></div>
                  <div className="mockup-screen-btn"></div>
                </div>
              </div>
            </div>
            <div className="mockup-laptop">
              <div className="mockup-laptop-screen">
                <div className="mockup-laptop-sidebar">
                  <div className="mockup-sidebar-icon"></div>
                  <div className="mockup-sidebar-icon"></div>
                  <div className="mockup-sidebar-icon"></div>
                  <div className="mockup-sidebar-icon"></div>
                </div>
                <div className="mockup-laptop-content">
                  <div className="mockup-content-header"></div>
                  <div className="mockup-content-cards">
                    <div className="mockup-card"></div>
                    <div className="mockup-card"></div>
                    <div className="mockup-card"></div>
                    <div className="mockup-card"></div>
                  </div>
                </div>
              </div>
              <div className="mockup-laptop-base"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="offers-section">
        <div className="section-title">
          <h2>Votre Pack Tout Compris</h2>
          <p>Un abonnement unique pour accéder à toute la puissance E-Shops</p>
        </div>
        <div className="offers-grid">
        {offers.map((offer) => (
          <div
            key={offer.id}
            className={`offer-card-new ${selectedOffer === offer.id ? 'selected' : ''} ${offer.popular ? 'popular' : ''}`}
            onClick={() => setSelectedOffer(offer.id)}
          >
            {offer.popular && <div className="popular-banner">Offre complète</div>}

            <div className="offer-badge" style={{ background: offer.gradient }}>
              {offer.badgeIcon && <offer.badgeIcon size={16} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />}{offer.badge}
            </div>

            <div className="offer-header-new">
              <h3>{offer.title}</h3>
              <p className="offer-subtitle-new">{offer.subtitle}</p>
            </div>

            <p className="offer-description">{offer.description}</p>

            <div className="offer-features-new">
              {offer.features.map((feature, index) => (
                <div key={index} className="feature-item-new">
                  <span className="feature-icon-new"><feature.icon size={24} /></span>
                  <div className="feature-text">
                    <strong>{feature.title}</strong>
                    <p>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="offer-advantages">
              {offer.advantages.map((adv, index) => (
                <div key={index} className="advantage-item">{adv}</div>
              ))}
            </div>

            <div className="offer-pricing-new">
              {offer.hasPaymentOptions ? (
                <div className="payment-options-container">
                  <p className="payment-options-title">Choisissez votre formule de paiement :</p>
                  <div className="payment-options-grid">
                    {offer.paymentOptions.map((option) => (
                      <div
                        key={option.id}
                        className={`payment-option-card ${selectedOffer === offer.id && selectedPaymentOption === option.id ? 'selected-option' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOffer(offer.id);
                          setSelectedPaymentOption(option.id);
                        }}
                      >
                        <div className="payment-option-header">
                          <h4>{option.name}</h4>
                          <span className="payment-option-badge">{option.badge}</span>
                        </div>
                        <p className="payment-option-description">{option.description}</p>
                        <div className="price-breakdown-new">
                          <div className="price-line">
                            <span>{option.setupFeeLabel || 'Frais de dossier'}</span>
                            <strong>{option.setupFee}€</strong>
                          </div>
                          {option.developmentCost > 0 && (
                            <div className="price-line">
                              <span>Développement</span>
                              <strong>{option.developmentCost}€</strong>
                            </div>
                          )}
                          <div className="price-line total">
                            <span>Total initial</span>
                            <strong>{option.setupFee + (option.developmentCost || 0)}€</strong>
                          </div>
                        </div>
                        <div className="price-display">
                          <span className="price-main">{option.price}€</span>
                          <span className="price-period">{option.priceLabel}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {offer.setupFee > 0 && (
                    <div className="price-breakdown-new">
                      <div className="price-line">
                        <span>Frais de dossier</span>
                        <strong>{offer.setupFee}€</strong>
                      </div>
                      {offer.developmentCost > 0 && (
                        <div className="price-line">
                          <span>Développement</span>
                          <strong>{offer.developmentCost}€</strong>
                        </div>
                      )}
                      <div className="price-line total">
                        <span>Total initial</span>
                        <strong>{offer.setupFee + (offer.developmentCost || 0)}€</strong>
                      </div>
                    </div>
                  )}
                  <div className="price-display">
                    <span className="price-main">{offer.price}€</span>
                    <span className="price-period">{offer.priceLabel}</span>
                  </div>
                  {offer.commitment ? (
                    <div className="commitment-badge">
                      <Lock size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Engagement {offer.commitment}
                    </div>
                  ) : (
                    <div className="no-commitment-badge">
                      <Unlock size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Sans engagement
                    </div>
                  )}
                </>
              )}
            </div>

            <button
              type="button"
              className="btn-select-offer"
              style={{
                background: selectedOffer === offer.id ? offer.gradient : '#f5f5f5',
                color: selectedOffer === offer.id ? 'white' : '#666'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedOffer(offer.id);
                scrollToForm();
              }}
            >
              {selectedOffer === offer.id ? '✓ Sélectionnée' : 'Choisir cette offre'}
            </button>
          </div>
        ))}
        </div>
      </div>

      {selectedOffer && (
        <div ref={formSectionRef} className="signup-form-section">
          <div className="form-header-new">
            <h2>Accédez à votre Pack Complet</h2>
            <p><Gift size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> <strong style={{ color: selectedOfferData.color }}>Tout inclus</strong> : utilisateurs illimités, +15 modules, mises à jour incluses — 599€/mois</p>
          </div>

          <form onSubmit={handleSubmit} className="signup-form-new">
            <SiretLookup
              compact
              initialSiret={formData.numero_siret}
              onCompanyFound={(data) => {
                setFormData(prev => ({
                  ...prev,
                  nom: data.dirigeant_nom || prev.nom,
                  entreprise: data.entreprise || prev.entreprise,
                  adresse: data.adresse || prev.adresse,
                  numero_siret: data.numero_siret || prev.numero_siret
                }));
              }}
            />
            <div className="form-columns">
              <div className="form-group">
                <label>Nom complet *</label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  className={errors.nom ? 'error' : ''}
                  placeholder="Jean Dupont"
                />
                {errors.nom && <span className="error-message">{errors.nom}</span>}
              </div>

              <div className="form-group">
                <label>Email professionnel *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="jean@entreprise.com"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
            </div>

            <div className="form-columns">
              <div className="form-group">
                <label>Téléphone *</label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleInputChange}
                  className={errors.telephone ? 'error' : ''}
                  placeholder="06 12 34 56 78"
                />
                {errors.telephone && <span className="error-message">{errors.telephone}</span>}
              </div>

              <div className="form-group">
                <label>Nom de l'entreprise *</label>
                <input
                  type="text"
                  name="entreprise"
                  value={formData.entreprise}
                  onChange={handleInputChange}
                  className={errors.entreprise ? 'error' : ''}
                  placeholder="Mon Entreprise SAS"
                />
                {errors.entreprise && <span className="error-message">{errors.entreprise}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Adresse complète *</label>
              <textarea
                name="adresse"
                value={formData.adresse}
                onChange={handleInputChange}
                className={errors.adresse ? 'error' : ''}
                placeholder="123 Rue de la République, 75001 Paris, France"
                rows="2"
              />
              {errors.adresse && <span className="error-message">{errors.adresse}</span>}
            </div>

            <div className="form-group">
              <label>Numéro SIRET (optionnel)</label>
              <input
                type="text"
                name="numero_siret"
                value={formData.numero_siret}
                onChange={handleInputChange}
                placeholder="123 456 789 00012"
              />
            </div>

            <div className="form-group">
              <label>Code partenaire ou code promotionnel (optionnel)</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <input
                  type="text"
                  name="code_promo"
                  value={formData.code_promo}
                  onChange={(e) => {
                    handleInputChange(e);
                    if (promoCodeApplied) {
                      setPromoCodeApplied(null);
                      setPromoCodeError('');
                    }
                  }}
                  placeholder="Entrez votre code ici"
                  style={{
                    borderColor: promoCodeApplied ? '#10b981' : promoCodeError ? '#ef4444' : '#667eea',
                    backgroundColor: promoCodeApplied ? '#f0fdf4' : '#f8f9ff',
                    flex: 1
                  }}
                />
                <button
                  type="button"
                  onClick={applyPromoCode}
                  disabled={promoCodeLoading || !formData.code_promo.trim()}
                  style={{
                    padding: '12px 20px',
                    background: promoCodeApplied ? '#10b981' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: promoCodeLoading || !formData.code_promo.trim() ? 'not-allowed' : 'pointer',
                    opacity: promoCodeLoading || !formData.code_promo.trim() ? 0.7 : 1,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {promoCodeLoading ? '...' : promoCodeApplied ? '✓ Appliqué' : 'Appliquer'}
                </button>
              </div>
              {promoCodeError && (
                <span style={{
                  fontSize: '12px',
                  color: '#ef4444',
                  marginTop: '5px',
                  display: 'block'
                }}>
                  <XCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> {promoCodeError}
                </span>
              )}
              {promoCodeApplied && (
                <div style={{
                  marginTop: '10px',
                  padding: '15px',
                  backgroundColor: '#f0fdf4',
                  borderRadius: '10px',
                  border: '2px solid #10b981'
                }}>
                  <div style={{ fontWeight: '600', color: '#10b981', marginBottom: '10px' }}>
                    <CheckCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Code "{promoCodeApplied.code}" appliqué avec succès !
                  </div>
                  <div style={{ fontSize: '13px', color: '#166534' }}>
                    {promoCodeApplied.description && (
                      <div style={{ marginBottom: '8px', fontStyle: 'italic' }}>
                        {promoCodeApplied.description}
                      </div>
                    )}
                    {promoCodeApplied.savings > 0 && (
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Frais initiaux :</strong>{' '}
                        <span style={{ color: '#ef4444', textDecoration: 'line-through' }}>{promoCodeApplied.originalTotal}€</span> →{' '}
                        <span style={{ fontWeight: 'bold', color: '#10b981' }}>{promoCodeApplied.newTotal}€</span>{' '}
                        <span style={{ color: '#10b981' }}>(-{promoCodeApplied.savings}€)</span>
                      </div>
                    )}
                    {promoCodeApplied.monthlySavings > 0 && (
                      <div style={{ marginBottom: '5px' }}>
                        <strong>Abonnement mensuel :</strong>{' '}
                        <span style={{ color: '#ef4444', textDecoration: 'line-through' }}>{promoCodeApplied.originalMonthly}€/mois</span> →{' '}
                        <span style={{ fontWeight: 'bold', color: '#10b981' }}>{promoCodeApplied.newMonthly}€/mois</span>{' '}
                        <span style={{ color: '#10b981' }}>(-{promoCodeApplied.subscriptionDiscount}%)</span>
                      </div>
                    )}
                    {(promoCodeApplied.savings > 0 || promoCodeApplied.monthlySavings > 0) && (
                      <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#dcfce7', borderRadius: '5px' }}>
                        <strong>Total économisé :</strong>
                        {promoCodeApplied.savings > 0 && ` ${promoCodeApplied.savings}€ sur les frais initiaux`}
                        {promoCodeApplied.savings > 0 && promoCodeApplied.monthlySavings > 0 && ' + '}
                        {promoCodeApplied.monthlySavings > 0 && `${(promoCodeApplied.monthlySavings * 36).toLocaleString('fr-FR')}€ sur 36 mois`}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {!promoCodeApplied && !promoCodeError && (
                <span style={{
                  fontSize: '12px',
                  color: '#667eea',
                  marginTop: '5px',
                  display: 'block'
                }}>
                  <Gem size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Entrez votre code partenaire pour bénéficier d'une réduction sur les frais initiaux
                </span>
              )}
            </div>

            <div className="payment-info-banner premium">
              <div className="banner-icon"><CreditCard size={24} /></div>
              <div className="banner-content">
                <h4>Paiement sécurisé par Stripe</h4>
                {promoCodeApplied ? (
                  <p>
                    Le paiement initial de{' '}
                    {promoCodeApplied.savings > 0 ? (
                      <>
                        <span style={{ textDecoration: 'line-through', color: '#ef4444' }}>{promoCodeApplied.originalTotal}€</span>{' '}
                        <strong style={{ color: '#10b981' }}>{promoCodeApplied.newTotal}€</strong>
                        <span style={{ color: '#10b981', fontWeight: '600' }}> (-{promoCodeApplied.savings}€)</span>
                      </>
                    ) : (
                      <strong>{promoCodeApplied.originalTotal}€</strong>
                    )}
                    {' '}sera effectué maintenant, puis{' '}
                    {promoCodeApplied.monthlySavings > 0 ? (
                      <>
                        <span style={{ textDecoration: 'line-through', color: '#ef4444' }}>{promoCodeApplied.originalMonthly}€</span>{' '}
                        <strong style={{ color: '#10b981' }}>{promoCodeApplied.newMonthly}€/mois</strong>
                      </>
                    ) : (
                      <strong>{getSelectedOfferDetails().price}€/mois</strong>
                    )}
                    {' '}à partir du mois prochain.
                  </p>
                ) : getSelectedOfferDetails().setupFee > 0 ? (
                  <p>Le paiement initial de {getSelectedOfferDetails().setupFee + (getSelectedOfferDetails().developmentCost || 0)}€ sera effectué maintenant, puis {getSelectedOfferDetails().price}€/mois à partir du mois prochain.</p>
                ) : (
                  <p>L'abonnement de {getSelectedOfferDetails().price}€/mois démarrera immédiatement après validation.</p>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="btn-submit-new"
              disabled={loading}
              style={{ background: selectedOfferData.gradient }}
            >
              {loading ? 'Traitement en cours...' : 'Procéder au paiement'}
            </button>

            <p className="terms-text">
              En créant un compte, vous acceptez nos <a href="#">conditions d'utilisation</a> et notre <a href="#">politique de confidentialité</a>.
            </p>
          </form>
        </div>
      )}

      <div className="signup-footer-new">
        <p>Déjà client ? <a href="/login">Se connecter</a></p>
        <button
          onClick={handleInstallApp}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '25px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
          }}
        >
          <Smartphone size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Télécharger l'espace client E-Shops
        </button>
        <div style={{ marginTop: '15px' }}>
          <a
            href="https://e-shop-pro.fr"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#a5b4fc',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            ← Retourner vers le site
          </a>
        </div>

        {/* Trust Badges */}
        <div className="trust-badges">
          <div className="trust-badge">
            <span className="trust-badge-icon"><Lock size={20} /></span>
            <span className="trust-badge-text">Paiement<br/>sécurisé</span>
          </div>
          <div className="trust-badge">
            <span className="trust-badge-icon">🇫🇷</span>
            <span className="trust-badge-text">Hébergement<br/>en France</span>
          </div>
          <div className="trust-badge">
            <span className="trust-badge-icon"><MessageSquare size={20} /></span>
            <span className="trust-badge-text">Support<br/>réactif</span>
          </div>
          <div className="trust-badge">
            <span className="trust-badge-icon"><Zap size={20} /></span>
            <span className="trust-badge-text">Mise en ligne<br/>rapide</span>
          </div>
        </div>
      </div>

      {/* Modal d'instructions pour iOS */}
      {showIOSInstructions && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowIOSInstructions(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '20px',
              padding: '30px',
              maxWidth: '400px',
              width: '90%',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowIOSInstructions(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#999'
              }}
            >
              ✕
            </button>

            <h2 style={{ marginBottom: '20px', color: '#333' }}>Installer E-Shops Connect</h2>
            <p style={{ marginBottom: '20px', color: '#666' }}>Pour installer l'application sur votre iPhone/iPad :</p>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{
                  minWidth: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '15px',
                  fontWeight: 'bold'
                }}>1</div>
                <div>Appuyez sur le bouton <strong>Partager</strong> en bas de l'écran</div>
              </div>

              <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'flex-start' }}>
                <div style={{
                  minWidth: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '15px',
                  fontWeight: 'bold'
                }}>2</div>
                <div>Faites défiler et sélectionnez <strong>"Sur l'écran d'accueil"</strong></div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{
                  minWidth: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '15px',
                  fontWeight: 'bold'
                }}>3</div>
                <div>Appuyez sur <strong>"Ajouter"</strong> en haut à droite</div>
              </div>
            </div>

            <button
              onClick={() => setShowIOSInstructions(false)}
              style={{
                width: '100%',
                padding: '12px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              J'ai compris
            </button>
          </div>
        </div>
      )}

      <InstallPWA />
    </div>
  );
}

export default Signup;
